import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import UpgradeModal from '@/components/UpgradeModal';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

const YOUTUBE_DEMO_URL = 'https://www.youtube.com/watch?v=W3oNusggjQw';

function navigateToApp() {
  window.location.href = '/app';
}

// Reusable header. Learn More is aimed at prospects, so the auth (Sign In /
// avatar) controls live on the landing + /app pages only. Header carries
// Pricing + Try it for Free here.
function LearnMoreHeader({ onPricingClick }) {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80"
      data-testid="learn-more-header"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-3 no-underline">
            <img
              src="/assets/logo.png"
              alt="Cash Forth Logo"
              className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="font-heading font-bold text-lg tracking-tight text-black">
                Cash Forth
              </h1>
              <p className="text-xs text-gray-500 hidden sm:block">
                Cash Flow Forecast + Unlimited What Ifs
              </p>
            </div>
          </a>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onPricingClick}
              data-testid="learn-pricing-btn"
              className="border-black/20 text-black hover:bg-black/5"
            >
              Pricing
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={navigateToApp}
              data-testid="learn-try-free-btn"
              className="border-black/20 text-black hover:bg-black/5"
            >
              Try it for Free
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default function LearnMorePage({ devMode = false }) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useDocumentMeta({
    title: 'Learn More — Cash Forth',
    description: 'Watch the Cash Forth demo and learn how cash flow forecasting helps you avoid overdrafts and plan ahead with confidence.',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openDemo = () => {
    window.open(YOUTUBE_DEMO_URL, '_blank', 'noopener,noreferrer');
  };

  // Shared CTA-button size (mirrors "Start Forecasting" on landing):
  // size="lg" + text-base + px-8 py-5 + rounded-md.
  const ctaBtnClass = 'text-base px-8 py-5 rounded-md';

  return (
    <div className="min-h-screen bg-white text-black" data-testid="learn-more-page">
      <LearnMoreHeader onPricingClick={() => setUpgradeOpen(true)} />
      {/* Spacer for fixed header */}
      <div className="h-16" />

      {/* Article body — book-like margins, comfortable line length. */}
      <article
        className="max-w-2xl mx-auto px-6 sm:px-8 py-12 md:py-16 font-heading text-black"
        data-testid="learn-more-article"
      >
        {/* H1 */}
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight">
          Why Cash Forth Exists
        </h1>

        <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-800">
          Cash Forth is focused on one thing: it shows you exactly where your
          money is headed, before it happens. It&rsquo;s a lightweight
          alternative to spreadsheets, with no complicated setup &mdash; just a
          clean, accurate forecast of your future cash balance based on the
          transactions you already know are coming.
        </p>

        <p className="mt-6 text-base sm:text-lg leading-relaxed text-gray-800">
          If you&rsquo;ve ever wondered:
        </p>
        <ul className="mt-3 pl-6 list-disc space-y-1.5 text-base sm:text-lg leading-relaxed text-gray-800">
          <li>&ldquo;How much extra can I put toward debt this month?&rdquo;</li>
          <li>&ldquo;How much can I move into savings without risking overdraft?&rdquo;</li>
          <li>&ldquo;Am I keeping too much idle cash in checking?&rdquo;</li>
          <li>&ldquo;Will I run out of money on my current trajectory?&rdquo;</li>
        </ul>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          Cash Forth gives you the answer in seconds.
        </p>

        {/* H2 */}
        <h2 className="mt-12 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
          Optimize Your Cash Flow &mdash; Without Guessing
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          Cash Forth helps you avoid two of the most common cash flow problems:
        </p>

        <ol className="mt-5 space-y-6 list-decimal pl-6 text-base sm:text-lg leading-relaxed text-gray-800">
          <li>
            <span className="font-semibold">Wasting excess cash in checking.</span>{' '}
            Some people keep a large buffer in checking &ldquo;just in
            case.&rdquo; But that buffer earns nothing. Cash Forth shows you:
            <ul className="mt-2 pl-6 list-disc space-y-1.5">
              <li>how much you can safely move to high yield savings, investments, or debt pay-down</li>
              <li>when you can move it</li>
              <li>and how much buffer you actually need</li>
            </ul>
            <p className="mt-3">
              You get to keep your safety net &mdash; without leaving
              unnecessary money on the table.
            </p>
          </li>
          <li>
            <span className="font-semibold">Catching overdrafts before they happen.</span>{' '}
            If your balance is going to dip dangerously low next week or next
            month, Cash Forth shows you today. You can move money ahead of
            time, instead of reacting after the fact.
          </li>
        </ol>

        {/* H2 */}
        <h2 className="mt-12 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
          How It Works
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          No bank syncing, and no AI &mdash; you are in the driver&rsquo;s
          seat. Add transactions, choose recurrence types (one time, monthly,
          biweekly, etc.) and the forecast is generated automatically.
        </p>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          You can also:
        </p>
        <ul className="mt-3 pl-6 list-disc space-y-1.5 text-base sm:text-lg leading-relaxed text-gray-800">
          <li>Customize future variable amounts for recurring transactions</li>
          <li>Include start/end dates for transactions</li>
          <li>Set balance threshold alerts</li>
          <li>Select time horizon view: 30 days, 90 days, 6 months, 1 year, 2 years</li>
          <li>View your forecast in ledger or chart format</li>
          <li>Create sporadic recurring transactions with custom dates/amounts</li>
        </ul>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          Cash Forth gives you the autonomy and flexibility of spreadsheets,
          but with no skills required. The app does all the heavy lifting
          &mdash; you just provide the inputs.
        </p>

        {/* H2 */}
        <h2 className="mt-12 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
          Intentionally Minimal
        </h2>
        <ul className="mt-4 pl-6 list-disc space-y-1.5 text-base sm:text-lg leading-relaxed text-gray-800">
          <li>No onboarding maze</li>
          <li>No required categories</li>
          <li>No &ldquo;set up your budget&rdquo; ceremony</li>
          <li>No financial jargon</li>
          <li>No ads, no noise</li>
        </ul>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          You can start forecasting in under a minute. And because the tool is
          built around clarity, every interaction is fast, predictable, and
          easy to undo or adjust.
        </p>

        {/* H2 */}
        <h2 className="mt-12 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
          A Companion to the Tools You Already Use
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          Cash Forth isn&rsquo;t trying to replace your budgeting app or net
          worth tracker. Cash Forth is the missing piece: It shows you the
          timing &mdash; the part most tools ignore. Use it alongside whatever
          else you&rsquo;re already using. It&rsquo;s simple, focused, and
          designed to fit into your financial workflow without overwhelming it.
        </p>

        {/* H2 */}
        <h2 className="mt-12 text-2xl sm:text-3xl font-bold leading-tight tracking-tight">
          Try It at Your Own Pace
        </h2>
        <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-800">
          You can explore the tool for free &mdash; no sign up required. When
          you&rsquo;re ready to save your forecast and return to it later, you
          can create an account and subscribe. Until then, feel free to click
          around, experiment, and see how Cash Forth could fit into your
          financial life.
        </p>

        {/* Button row */}
        <div
          className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4"
          data-testid="learn-more-cta-row"
        >
          <Button
            size="lg"
            variant="outline"
            onClick={openDemo}
            data-testid="learn-watch-demo-btn"
            className={`border-black/20 text-black hover:bg-black/5 ${ctaBtnClass}`}
          >
            Watch a Demo
          </Button>
          <Button
            size="lg"
            onClick={navigateToApp}
            data-testid="learn-start-forecasting-btn"
            className={`bg-black text-white hover:bg-black/85 ${ctaBtnClass}`}
          >
            Start Forecasting
          </Button>
        </div>
      </article>

      {/* Minimal footer (same as landing) */}
      <footer className="py-6 text-center text-xs text-gray-400 border-t border-gray-100">
        &copy; 2026 Cash Forth. All rights reserved.
      </footer>

      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        mode="pricing"
        devMode={devMode}
      />
    </div>
  );
}
